import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Sparkles, Share2, FileDown } from "lucide-react";

const MV_ORDER = [
  { key: "trust", title: "Trust" },
  { key: "follow", title: "Follow" },
  { key: "imitate", title: "Imitate" },
  { key: "teach", title: "Teach Others" },
];

/** Side-by-side movement bar: Week 1 (ghosted) and Week 11 (primary). */
function MovementCompareBar({ title, start, finish }) {
  const pctStart = Math.max(0, Math.min(25, start?.score || 0)) / 25;
  const pctFinish = Math.max(0, Math.min(25, finish.score)) / 25;
  const change = start ? finish.score - start.score : null;
  const sign = change > 0 ? "+" : "";
  const changeColor = change == null ? "text-muted-foreground" : change > 0 ? "text-primary" : change < 0 ? "text-destructive/80" : "text-muted-foreground";
  return (
    <div data-testid={`compare-movement-${title.toLowerCase().split(' ')[0]}`}>
      <div className="flex items-baseline justify-between mb-2">
        <div className="font-serif text-lg">{title}</div>
        <div className="text-xs text-muted-foreground">
          <span className="tabular-nums text-foreground">{start?.score ?? "—"}</span>
          <span className="mx-1.5">→</span>
          <span className="tabular-nums text-primary font-serif text-lg">{finish.score}</span>
          <span className="mx-1">/</span>25
          <span className={`ml-3 tabular-nums font-serif text-lg ${changeColor}`}>
            {change == null ? "" : `${sign}${change}`}
          </span>
        </div>
      </div>
      {start && (
        <div className="relative h-2 bg-secondary/60 mb-1 overflow-hidden">
          <div className="absolute inset-y-0 left-0 bg-muted-foreground/40 transition-all" style={{ width: `${pctStart * 100}%` }} />
        </div>
      )}
      <div className="relative h-2 bg-secondary/60 overflow-hidden">
        <div className="absolute inset-y-0 left-0 bg-primary transition-all" style={{ width: `${pctFinish * 100}%` }} />
      </div>
      <div className="flex justify-between text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
        <span>{start ? "Week 1" : ""}</span>
        <span>{finish.label}</span>
      </div>
    </div>
  );
}

export default function AssessmentCompare() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [content, setContent] = useState(null);
  const [shareMode, setShareMode] = useState({ start: false, finish: false });
  const [summary, setSummary] = useState("");
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    try {
      const [cmp, c, startDoc, finishDoc] = await Promise.all([
        api.get("/assessment/compare"),
        api.get("/assessment/content"),
        api.get("/assessment/start").catch(() => ({ data: null })),
        api.get("/assessment/finish"),
      ]);
      setData(cmp.data);
      setSummary(cmp.data.growth_summary || "");
      setContent(c.data);
      setShareMode({
        start: !!startDoc?.data?.share_with_leader,
        finish: !!finishDoc?.data?.share_with_leader,
      });
    } catch {
      toast.error("You haven't submitted the finishing assessment yet.");
      nav("/app/assessment/finish", { replace: true });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggleShare = async (next) => {
    try {
      await Promise.all([
        data?.has_start ? api.post("/assessment/start/share", { share: next }) : Promise.resolve(),
        api.post("/assessment/finish/share", { share: next }),
      ]);
      setShareMode({ start: next, finish: next });
      toast.success(next ? "Shared with your group leader." : "Sharing turned off.");
    } catch {
      toast.error("Could not update sharing right now.");
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const { data: blob } = await api.get("/assessment/report.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "answering-the-call-growth-report.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not create your report right now.");
    } finally {
      setDownloading(false);
    }
  };

  const generateSummary = async () => {
    setGenerating(true);
    try {
      const { data: r } = await api.post("/assessment/growth-summary");
      setSummary(r.summary || "");
    } catch {
      toast.error("Could not generate a summary just now. Try again in a bit.");
    } finally {
      setGenerating(false);
    }
  };

  if (!data || !content) return <div className="py-20 text-center text-muted-foreground">Preparing…</div>;

  const startRef = data.start?.reflections || {};
  const finishRef = data.finish.reflections || {};
  const overallSign = data.overall_change == null ? "" : data.overall_change > 0 ? "+" : "";
  const sharing = shareMode.finish;

  return (
    <div className="max-w-3xl mx-auto">
      <p className="text-xs uppercase tracking-[0.2em] text-primary">Finishing Assessment · Comparison</p>
      <h1 className="font-serif text-5xl mt-3 leading-tight">What God has been doing in you.</h1>

      {!data.has_start && (
        <p className="mt-8 border-l-2 border-accent pl-5 italic text-muted-foreground">
          No Starting Assessment on file. We're showing your Week 11 numbers alone — but the honest work you did these eleven weeks still counts.
        </p>
      )}

      {/* Overall */}
      {data.has_start && (
        <section data-testid="compare-overall" className="mt-12 border border-border bg-card p-8">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Overall</div>
          <div className="flex items-baseline flex-wrap gap-4">
            <div>
              <div className="font-serif text-5xl tabular-nums">
                <span className="text-muted-foreground">{data.start.scores.overall}</span>
                <span className="mx-3 text-muted-foreground">→</span>
                <span className="text-primary">{data.finish.scores.overall}</span>
              </div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mt-2">
                out of 100
              </div>
            </div>
            <div className={`ml-auto font-serif text-3xl tabular-nums ${data.overall_change > 0 ? "text-primary" : data.overall_change < 0 ? "text-destructive/80" : "text-muted-foreground"}`}>
              {overallSign}{data.overall_change}
            </div>
          </div>
        </section>
      )}

      {/* Per movement */}
      <section className="mt-14 space-y-8" data-testid="compare-movements">
        <h2 className="font-serif text-2xl mb-6">By movement</h2>
        {MV_ORDER.map((mv) => (
          <MovementCompareBar
            key={mv.key}
            title={mv.title}
            start={data.start?.scores.movements[mv.key]}
            finish={data.finish.scores.movements[mv.key]}
          />
        ))}
      </section>

      {/* Biggest gains + Still growing */}
      {data.has_start && (
        <div className="mt-14 grid md:grid-cols-2 gap-10">
          <section data-testid="compare-gains">
            <h2 className="font-serif text-2xl mb-5">Where God has moved</h2>
            <ul className="space-y-5">
              {data.biggest_gains.every((d) => d.change <= 0) && (
                <li className="text-muted-foreground italic" data-testid="compare-gains-empty">
                  Your numbers held steady. Growth is often quiet — look at your reflections for what God has been doing.
                </li>
              )}
              {data.biggest_gains.filter((d) => d.change > 0).map((d) => (
                <li key={d.index} className="border-l-2 border-primary pl-5">
                  <p className="font-serif leading-relaxed">{d.text}</p>
                  <div className="text-xs text-muted-foreground mt-1.5 tabular-nums">
                    Week 1: {d.start} <span className="mx-2">→</span> Week 11: {d.finish}
                    <span className={`ml-3 ${d.change > 0 ? "text-primary" : "text-muted-foreground"}`}>
                      {d.change > 0 ? `+${d.change}` : d.change}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section data-testid="compare-still-growing">
            <h2 className="font-serif text-2xl mb-5">Where you're still growing</h2>
            <ul className="space-y-5">
              {data.still_growing.map((d) => (
                <li key={d.index} className="border-l-2 border-accent/50 pl-5">
                  <p className="font-serif leading-relaxed">{d.text}</p>
                  <div className="text-xs text-muted-foreground mt-1.5 tabular-nums">
                    Current: {d.finish} / 5 <span className="ml-2 italic">— a next step, not a failure.</span>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>
      )}

      {/* Words then and now */}
      <section className="mt-16 border-t border-border pt-10" data-testid="compare-reflections">
        <h2 className="font-serif text-2xl mb-6">Your words then and now</h2>
        {content.reflections_start.map((p) => (
          <div key={p.key} className="mb-10">
            <div className="text-sm text-muted-foreground mb-3">{p.prompt}</div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="border border-border p-5">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Week 1</div>
                <p className="font-serif italic leading-relaxed">{(startRef[p.key] || "—").trim() || "—"}</p>
              </div>
              <div className="border border-primary/40 p-5 bg-primary/5">
                <div className="text-xs uppercase tracking-widest text-primary mb-2">Week 11</div>
                <p className="font-serif italic leading-relaxed">{(finishRef[p.key] || "—").trim() || "—"}</p>
              </div>
            </div>
          </div>
        ))}
        {/* Finish-only prompts (r4-r6) */}
        {content.reflections_finish.slice(3).map((p) => (
          <div key={p.key} className="mb-8">
            <div className="text-sm text-muted-foreground mb-2">{p.prompt}</div>
            <p className="font-serif italic leading-relaxed border-l-2 border-primary/40 pl-5">
              {(finishRef[p.key] || "—").trim() || "—"}
            </p>
          </div>
        ))}
      </section>

      <p className="mt-10 text-sm text-muted-foreground italic max-w-2xl border-l-2 border-accent/60 pl-5">
        {data.interpretation_note}
      </p>

      {/* AI Growth Summary */}
      <section className="mt-14 border border-border bg-card p-8" data-testid="compare-growth-summary">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-accent mb-1">Reflection coach</div>
            <h3 className="font-serif text-2xl">Your growth summary</h3>
          </div>
          {!summary && (
            <Button onClick={generateSummary} disabled={generating} data-testid="compare-generate-summary" className="rounded-full">
              {generating
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Writing…</>
                : <><Sparkles className="w-4 h-4 mr-2" /> Generate my growth summary</>}
            </Button>
          )}
        </div>
        {summary ? (
          <p data-testid="compare-growth-summary-text" className="whitespace-pre-wrap leading-relaxed">{summary}</p>
        ) : (
          <p className="text-sm text-muted-foreground max-w-2xl">
            Optional: a short, warm, grace-centered note that celebrates specific growth, names one area to keep pressing in, and suggests one next step. Based only on what you wrote and scored.
          </p>
        )}
      </section>

      {/* Keepsake PDF */}
      <section className="mt-10 border border-border bg-card p-6 flex items-center justify-between gap-4 flex-wrap" data-testid="compare-pdf-section">
        <div className="flex items-start gap-3">
          <FileDown className="w-5 h-5 text-primary mt-0.5" />
          <div>
            <div className="font-serif text-lg">Keepsake growth report</div>
            <div className="text-sm text-muted-foreground max-w-md">
              A printable PDF with your scores, {summary ? "growth summary " : ""}and reflections from Week 1 and Week 11.
              {!summary && " Generate your growth summary above to include it."}
            </div>
          </div>
        </div>
        <Button onClick={downloadPdf} disabled={downloading} variant="outline" className="rounded-full" data-testid="compare-download-pdf">
          {downloading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing…</> : <><FileDown className="w-4 h-4 mr-2" /> Download PDF</>}
        </Button>
      </section>

      {/* Share with leader */}
      <section className="mt-10 border border-border bg-card p-6 flex items-center justify-between gap-4 flex-wrap" data-testid="compare-share-section">
        <div className="flex items-start gap-3">
          <Share2 className="w-5 h-5 text-primary mt-0.5" />
          <div>
            <div className="font-serif text-lg">Share with my leader</div>
            <div className="text-sm text-muted-foreground max-w-md">
              If turned on, your group leader can see both assessments and the comparison. Off by default — your choice to open the door.
            </div>
          </div>
        </div>
        <Switch
          data-testid="compare-share-toggle"
          checked={sharing}
          onCheckedChange={toggleShare}
        />
      </section>

      {/* Closing blessing */}
      <section className="mt-16 border-t border-border pt-12 text-center">
        <p className="font-serif text-3xl italic text-primary leading-snug">{data.closing_blessing}</p>
        <div className="mt-8 flex gap-3 justify-center flex-wrap">
          <Button asChild className="rounded-full" data-testid="compare-cta-timothy">
            <Link to="/app/settings">Start discipling my Timothy</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-full">
            <Link to="/app">Back to all weeks</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
